const SHEET_ID = '1brW8mOok9lBn_lUlN0yfra0miWgwhH7DMePDB2n6J4A';
const SHEET_TITLE = 'Raw';
const FULL_URL = `https://docs.google.com/spreadsheets/d/${SHEET_ID}/gviz/tq?sheet=${SHEET_TITLE}`;

// Cache configuration
let cachedData = null;
let cacheTime = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Fetches and parses data from Google Sheets
 * @param {boolean} forceRefresh - Force bypass cache
 * @returns {Promise<{players: Object, games: Object}>}
 */
export const readAllData = async (forceRefresh = false) => {
    const now = Date.now();
    
    // Return cached data if available and not expired
    if (!forceRefresh && cachedData && (now - cacheTime < CACHE_DURATION)) {
        return cachedData;
    }
    
    try {
        const response = await fetch(FULL_URL);
        if (!response.ok) {
            throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
        }
        
        const rawData = await response.text();
        
        // Parse the JSON data more robustly
        let data;
        const jsonMatch = rawData.match(/google\.visualization\.Query\.setResponse\((.*)\);?$/s);
        if (jsonMatch) {
            data = JSON.parse(jsonMatch[1]);
        } else {
            // Fallback to original parsing method
            data = JSON.parse(rawData.substring(47).slice(0, -2));
        }
        
        // Extract rows from data.table
        const rows = data.table.rows;
        
        // Parse data once, create both dictionaries
        const players = createPlayers(rows);
        const games = createGames(rows);
        
        // Cache the results
        cachedData = { players, games };
        cacheTime = now;
        
        return cachedData;
    } catch (error) {
        console.error('Error fetching sheet data:', error);
        throw error;
    }
};

/**
 * Reads only player data (uses cached data if available)
 */
export const readPlayers = async (forceRefresh = false) => {
    const data = await readAllData(forceRefresh);
    return data.players;
};

/**
 * Reads only game data (uses cached data if available)
 */
export const readGames = async (forceRefresh = false) => {
    const data = await readAllData(forceRefresh);
    return data.games;
};

/**
 * Calculates the summary statistics used by the games page.
 */
export const getGameSummaries = (gamesData) => {
    if (!gamesData || typeof gamesData !== 'object') {
        return {
            numberOfGames: null,
            averagePot: null,
            peakNight: null
        };
    }

    const games = Object.values(gamesData);
    const pots = games
        .map((game) => Number(game.moneyOnTable))
        .filter((pot) => Number.isFinite(pot));

    return {
        numberOfGames: games.length,
        averagePot: pots.length > 0
            ? pots.reduce((total, pot) => total + pot, 0) / pots.length
            : null,
        peakNight: pots.length > 0 ? Math.max(...pots) : null
    };
};

/**
 * Gets the last game's data formatted for the GameRecap component
 */
export const getLastGameRecap = async (forceRefresh = false) => {
    const data = await readAllData(forceRefresh);
    const games = data.games;
    
    if (!games || Object.keys(games).length === 0) {
        return null;
    }
    
    // Find the highest game number (most recent game)
    const gameNumbers = Object.keys(games).map(Number).sort((a, b) => b - a);
    const lastGameNumber = gameNumbers[0];
    const lastGame = games[lastGameNumber];
    
    // Sort players by winnings (descending)
    const sortedPlayers = [...lastGame.playerDetails].sort((a, b) => b.winnings - a.winnings);
    
    // Create rankings array with top 3 winners and biggest loser
    const rankings = [];
    
    // Add top 3 winners
    for (let i = 0; i < Math.min(3, sortedPlayers.length); i++) {
        rankings.push({
            rank: i + 1,
            name: sortedPlayers[i].playerName,
            winnings: sortedPlayers[i].winnings
        });
    }
    
    // Add biggest loser (if there are more than 3 players and someone lost money)
    if (sortedPlayers.length > 3) {
        const biggestLoser = sortedPlayers[sortedPlayers.length - 1];
        if (biggestLoser.winnings < 0) {
            rankings.push({
                rank: 4,
                name: biggestLoser.playerName,
                winnings: biggestLoser.winnings
            });
        }
    }
    
    return {
        gameNumber: lastGame.gameNumber,
        date: lastGame.date,
        location: lastGame.location,
        rankings
    };
};

/**
 * Gets player streaks data (hot and due players based on last 5 games)
 */

export const getPlayerStreaks = async (forceRefresh = false) => {
    const data = await readAllData(forceRefresh);
    const games = data.games;
    
    if (!games || Object.keys(games).length === 0) {
        return null;
    }
    
    // Get the last 5 games (sorted by game number descending)
    const gameNumbers = Object.keys(games).map(Number).sort((a, b) => b - a);
    const last5GameNumbers = gameNumbers.slice(0, 5);
    
    // Calculate total winnings per player over last 5 games
    const playerWinnings = {};
    
    last5GameNumbers.forEach(gameNum => {
        const game = games[gameNum];
        game.playerDetails.forEach(player => {
            if (!playerWinnings[player.playerName]) {
                playerWinnings[player.playerName] = 0;
            }
            playerWinnings[player.playerName] += player.winnings;
        });
    });
    
    // Convert to array and sort by winnings
    const playerArray = Object.entries(playerWinnings).map(([name, winnings]) => ({
        name,
        winnings
    }));
    
    playerArray.sort((a, b) => b.winnings - a.winnings);
    
    // Hot player is the one with highest winnings
    const hotPlayer = playerArray.length > 0 
        ? { name: playerArray[0].name, winnings: playerArray[0].winnings }
        : { name: '...', winnings: null };
    
    // Due player is the one with lowest winnings (most negative)
    const duePlayer = playerArray.length > 0
        ? { name: playerArray[playerArray.length - 1].name, winnings: playerArray[playerArray.length - 1].winnings }
        : { name: '...', winnings: null };
    
    return {
        hotPlayer,
        duePlayer
    };
};

/**
 * Clears the cache (useful for forcing fresh data)
 */
export const clearCache = () => {
    cachedData = null;
    cacheTime = null;
};

/**
 * Creates players dictionary from sheet rows
 */
function createPlayers(rows) {
    const playersDict = {};
    
    rows.forEach(row => {
        // Handle null cells gracefully
        if (!row.c || !row.c[3] || !row.c[3].v) {
            return; // Skip rows without player name
        }

        const playerName = row.c[3].v;
        const playersPlayedAgainst = row.c[4]?.v ?? 0;
        const totalBuyIn = row.c[5]?.v ?? 0;
        const totalBuyBack = row.c[6]?.v ?? 0;
        const totalMoneyOut = row.c[8]?.v ?? 0;

        if (!playersDict[playerName]) {
            // Player not in dictionary, add them
            playersDict[playerName] = createPlayer(
                playerName,
                1,
                playersPlayedAgainst,
                totalBuyIn,
                totalBuyBack,
                totalBuyIn + totalBuyBack,
                totalMoneyOut
            );
        } else {
            // Player already in dictionary, update their stats
            const player = playersDict[playerName];
            
            // Updating core stats
            player.gamesPlayed += 1;
            player.numberPlayersPlayedAgainst += playersPlayedAgainst;
            player.totalBuyIn += totalBuyIn;
            player.totalBuyBack += totalBuyBack;
            player.totalMoneyIn += totalBuyIn + totalBuyBack;
            player.totalMoneyOut += totalMoneyOut;
            player.winnings = player.totalMoneyOut - player.totalMoneyIn;
            
            // Updating derived stats
            player.winningsPerGame = player.winnings / player.gamesPlayed;
            player.return = player.totalMoneyIn > 0 
                ? (player.winnings / player.totalMoneyIn) * 100 
                : 0;
            player.avgPlayersPlayedAgainst = player.numberPlayersPlayedAgainst / player.gamesPlayed;
        }
    });
    
    return playersDict;
}

/**
 * Creates a player object
 */
function createPlayer(playerName, gamesPlayed, numberPlayersPlayedAgainst, totalBuyIn, totalBuyBack, totalMoneyIn, totalMoneyOut) {
    const winnings = totalMoneyOut - totalMoneyIn;
    
    return {
        // Core stats
        playerName,
        gamesPlayed,
        numberPlayersPlayedAgainst,
        totalBuyIn,
        totalBuyBack,
        totalMoneyOut,

        // Derived stats
        avgPlayersPlayedAgainst: gamesPlayed > 0 
            ? numberPlayersPlayedAgainst / gamesPlayed 
            : 0,
        totalMoneyIn,
        winnings,
        winningsPerGame: gamesPlayed > 0 
            ? winnings / gamesPlayed 
            : 0,
        return: totalMoneyIn > 0 
            ? (winnings / totalMoneyIn) * 100 
            : 0
    };
}

/**
 * Creates games dictionary from sheet rows
 */
function createGames(rows) {
    const gamesDict = {};
    
    rows.forEach(row => {
        // Handle null cells gracefully
        if (!row.c || !row.c[1] || row.c[1].v === null || row.c[1].v === undefined) {
            return; // Skip rows without game number
        }

        // Extract game data from row
        const date = row.c[0]?.v ?? 'Unknown';
        const gameNumber = row.c[1].v;
        const location = row.c[2]?.v ?? 'Unknown';

        // Extract the player data from an instance of playing
        const playerName = row.c[3]?.v;
        if (!playerName) return; // Skip if no player name
        
        const playersPlayedAgainst = row.c[4]?.v ?? 0;
        const totalBuyIn = row.c[5]?.v ?? 0;
        const totalBuyBack = row.c[6]?.v ?? 0;
        const totalMoneyOut = row.c[8]?.v ?? 0;

        // Create the player object
        const player = createPlayer(
            playerName,
            1,
            playersPlayedAgainst,
            totalBuyIn,
            totalBuyBack,
            totalBuyIn + totalBuyBack,
            totalMoneyOut
        );

        // Create or update the game object
        if (!gamesDict[gameNumber]) {
            // Game not in dictionary, add it
            gamesDict[gameNumber] = createGame(
                gameNumber,
                date,
                location,
                1,
                totalMoneyOut,
                [player]
            );
        } else {
            // Game already in dictionary, update its stats
            const game = gamesDict[gameNumber];
            game.playersPlaying += 1;
            game.moneyOnTable += totalMoneyOut;
            game.playerDetails.push(player);
        }
    });
    
    return gamesDict;
}

/**
 * Creates a game object
 */
function createGame(gameNumber, date, location, playersPlaying, moneyOnTable, playerDetails) {
    return {
        gameNumber,
        date,
        location,
        playersPlaying,
        moneyOnTable,
        playerDetails
    };
}